import React from 'react'

interface BriefingStackIconProps {
  className?: string
  size?: number
  variant?: 'default' | 'active' | 'hover'
}

export function BriefingStackIcon({
  className = '',
  size,
  variant = 'default'
}: BriefingStackIconProps) {
  // Color variations based on state
  const colors = {
    default: {
      primary: '#94a3b8', // slate-400
      secondary: '#64748b', // slate-500
      accent: '#fb2ba9', // polari pink
      glow: 'rgba(251, 43, 169, 0.2)'
    },
    active: {
      primary: '#fb2ba9',
      secondary: '#e91e8c',
      accent: '#fb2ba9',
      glow: 'rgba(251, 43, 169, 0.4)'
    },
    hover: {
      primary: '#cbd5e1', // slate-300
      secondary: '#94a3b8', // slate-400
      accent: '#fb2ba9',
      glow: 'rgba(251, 43, 169, 0.3)'
    }
  }

  const currentColors = colors[variant]

  return (
    <>
      <style>{`
        @keyframes spread-back {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(2px); }
        }
        
        @keyframes spread-middle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(1px); }
        }
        
        @keyframes color-blink-back {
          0%, 100% { fill: ${currentColors.secondary}; }
          15%, 30% { fill: #CC339F; }
        }
        
        @keyframes color-blink-middle {
          0%, 100% { fill: ${currentColors.secondary}; }
          35%, 50% { fill: #6600FF; }
        }
        
        @keyframes color-blink-front {
          0% { fill: ${currentColors.primary}; }
          55%, 100% { fill: #00FFC0; }
        }
        
        .briefing-stack-hover .card-back {
          animation: spread-back 0.6s ease-in-out, color-blink-back 1.2s ease-in-out;
        }
        
        .briefing-stack-hover .card-middle {
          animation: spread-middle 0.6s ease-in-out, color-blink-middle 1.2s ease-in-out;
        }
        
        .briefing-stack-hover .card-front {
          animation: color-blink-front 1.2s ease-in-out forwards;
        }
      `}</style>

      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`${className} ${variant === 'hover' ? 'briefing-stack-hover' : ''}`}
      >
        {/* Glow effect (only visible when active) */}
        {variant === 'active' && (
          <circle
            cx="12"
            cy="12"
            r="10"
            fill={currentColors.glow}
            opacity="0.6"
          />
        )}

        {/* Back card (furthest) - Blinks #CC339F first */}
        <rect
          className="card-back"
          x="4"
          y="13"
          width="16"
          height="8"
          rx="2"
          fill={currentColors.secondary}
          opacity="0.4"
          style={{ transformOrigin: 'center' }}
        />

        {/* Middle card - Blinks #6600FF second */}
        <rect
          className="card-middle"
          x="4"
          y="9"
          width="16"
          height="9"
          rx="2"
          fill={currentColors.secondary}
          opacity="0.7"
          style={{ transformOrigin: 'center' }}
        />

        {/* Front card (active briefing) - Holds #00FFC0 last */}
        <rect
          className="card-front"
          x="4"
          y="5"
          width="16"
          height="10"
          rx="2"
          fill={currentColors.primary}
          stroke={variant === 'active' ? currentColors.accent : 'transparent'}
          strokeWidth="1.5"
          style={{ transformOrigin: 'center' }}
        />

        {/* Content lines on front card */}
        <line
          x1="7"
          y1="8"
          x2="17"
          y2="8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.6"
        />
        <line
          x1="7"
          y1="11"
          x2="14"
          y2="11"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.4"
        />

        {/* Accent dot (unread indicator) */}
        {variant === 'active' && (
          <circle
            cx="18"
            cy="7"
            r="2"
            fill={currentColors.accent}
          />
        )}
      </svg>
    </>
  )
}